<script setup lang="ts">
import type { IMainVideosItemDataClientDTO } from '~/shared/api/client/main-videos/main-videos.dto';
import MainVideosNewsItem from './MainVideosNewsItem.vue';
import GridItemQuad from '~/shared/ui/grid-item/GridItemQuad.vue';
import GridQuad from '~/shared/ui/grid/GridQuad.vue';
import { DEFAULT_PUBLISHER_STYLE_PROPS_VIDEO } from '~/shared/lib/publishers.const';
import type { TPublisherStyleProps } from '~/shared/lib/publishers.types';

const { $viewport } = useNuxtApp();

const props = withDefaults(
  defineProps<
    {
      items: IMainVideosItemDataClientDTO[];
    } & TPublisherStyleProps
  >(),
  DEFAULT_PUBLISHER_STYLE_PROPS_VIDEO,
);

const isModalOpened = ref(false);
const title = ref('');
const iframe = ref('');

const handleOpenPlayer = (data: { title: string; iframe: string }) => {
  title.value = data.title;
  iframe.value = data.iframe;
  isModalOpened.value = true;
};

const DATA = {
  title: 'Видео',
  links: [
    { name: 'Открытие', href: '/category/29' },
    { name: 'Проект', href: '/category/22' },
    { name: 'Аукцион', href: '/category/26' },
    { name: 'Инвестиции', href: '/category/6' },
    { name: 'Исследование рынка', href: '/category/3' },
  ],
  borderColor:
    props.borderColor || DEFAULT_PUBLISHER_STYLE_PROPS_VIDEO.borderColor,
  backgroundColor:
    props.backgroundColor ||
    DEFAULT_PUBLISHER_STYLE_PROPS_VIDEO.backgroundColor,
  textColor: props.textColor || DEFAULT_PUBLISHER_STYLE_PROPS_VIDEO.textColor,
};
</script>

<template>
  <div
    :class="$style.video"
    :style="{
      '--background-color': DATA.backgroundColor,
      '--text-color': DATA.textColor,
      '--border-color': DATA.borderColor,
    }"
  >
    <div :class="$style.video__header">
      <div :class="$style.video__headerInner">
        <div :class="$style.video__headerTitle">{{ DATA.title }}</div>
        <div :class="$style.video__headerLinks">
          <NuxtLink
            v-for="(item, key) in DATA.links"
            :key="`mainVideosLinks_${key}`"
            :class="$style.video__headerLinksItem"
            :to="item.href"
          >
            #{{ item.name }}
          </NuxtLink>
        </div>
      </div>
      <Button
        theme="white"
        size="small"
        to="https://cre.ru/events/reports/video"
        :class="$style.video__headerButton"
      >
        Смотреть все
      </Button>
    </div>

    <GridQuad v-if="items.length > 0" class="$style.video__grid">
      <GridItemQuad
        :key="`gridVideoItemBig`"
        :class="$style.video__gridItem"
        :col-span="3"
        :row-span="2"
        :border-color="DATA.borderColor"
      >
        <MainVideosNewsItem
          v-bind="items[0] as IMainVideosItemDataClientDTO"
          :class="$style.video__gridComponent"
          :text-color="DATA.textColor"
          is-image-on-top
          @open-player="handleOpenPlayer"
        />
      </GridItemQuad>
      <template v-if="$viewport.isGreaterOrEquals('mobile')">
        <template v-for="(item, i) in items" :key="`gridVideoItem${i}`">
          <GridItemQuad
            v-if="i !== 0"
            :class="$style.video__gridItem"
            :col-span="1"
            :row-span="1"
            :border-color="DATA.borderColor"
          >
            <MainVideosNewsItem
              :class="$style.video__gridLineItem"
              :text-color="DATA.textColor"
              v-bind="item"
              @open-player="handleOpenPlayer"
            />
          </GridItemQuad>
        </template>
      </template>
      <div v-else :class="$style.video__slider">
        <template v-for="(item, i) in items" :key="`gridVideoSliderItem${i}`">
          <MainVideosNewsItem
            v-if="i !== 0"
            :class="$style.video__slider__item"
            :text-color="DATA.textColor"
            v-bind="item"
            @open-player="handleOpenPlayer"
          />
        </template>
      </div>
    </GridQuad>

    <ModalVideo
      :is-visible="isModalOpened"
      :iframe="iframe"
      :title="title"
      @close="isModalOpened = false"
    />
  </div>
</template>

<style module lang="scss">
.video {
  background: var(--background-color);

  &__header {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    height: 64px;
    padding-left: var(--space-2-5x);
    padding-right: var(--space-2-5x);
    justify-content: space-between;
    border-bottom: 1px solid var(--border-color);

    &Inner {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      height: 100%;
      justify-content: space-between;
      flex-grow: 1;
    }

    &Title {
      @include text_h5;
      color: var(--text-color);
    }

    &Links {
      display: flex;
      gap: var(--space-3x);
      height: 100%;
      color: var(--color-red-2);
      margin: 0 auto;

      &Item {
        height: 100%;
        @include text_caption2_caps;
        color: var(--color-red-2);
        cursor: pointer;
        align-items: center;
        display: flex;
        text-decoration: none;
      }
    }

    &Button {
      height: 40px;
      background: var(--color-red-1);
      color: var(--color-white);
    }
  }
}

@media (hover: hover) {
  .video__headerLinksItem {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (min-width: 0px) and (max-width: 1379px) {
  .video {
    &__header {
      flex-direction: column;
      height: inherit;
      padding: var(--space-2-5x);
      gap: var(--space-2-5x);
      align-items: inherit;

      &Button {
        justify-content: center;
      }
    }
  }
}

@media (min-width: 768px) and (max-width: 1379px) {
  .video {
    &__header {
      &Links {
        gap: var(--space-2x);
        margin: inherit;
      }
    }
  }
}

@media (min-width: 0) and (max-width: 767px) {
  .video {
    &__header {
      flex-direction: column;
      height: inherit;
      padding: 20px;
      gap: var(--space-2-5x);
      align-items: inherit;

      &Inner {
        flex-direction: column;
        gap: var(--space-1-5x);
        align-items: inherit;
      }

      &Links {
        gap: var(--space-1-5x) var(--space-3x);
        margin: inherit;
        flex-wrap: wrap;
        align-items: flex-start;
      }

      &Button {
        justify-content: center;
      }
    }
  }
}

@media (max-width: 479px) {
  .video {
    &__header {
      padding: var(--space-2x);
    }

    &__slider {
      @include mobileSliderWrapper;

      &__item {
        @include mobileSliderItem;
        border-right: 1px solid var(--color-black-2);

        &:last-child {
          border-right: none;
        }
      }
    }
  }
}
</style>
